import { bindEvents, getRootOffsetBounds, getOffsetBounds, mq } from '../../../assets/js/helpers';

import AnimationController from '../../../assets/js/controller/animation';
import CursorController from '../../../assets/js/controller/cursor';
import ResizeController from '../../../assets/js/controller/resize';

const selector = 'cursor-trailing';

export default class CursorTrailing {

	constructor(section, options = {}) {
		this.el = {
			section,
			container: null
		};

		this.defaults = {
			lerp: {
				double: true,
				acceleration: 0.08,
				max: 0.24
			},
			offset: {
				x: 0,
				y: -30
			}
		};

		// merge default options with param options
		this.config = Object.assign({}, this.defaults, options);

		// bind events to class scope
		this.events = bindEvents(this);

		// class variable groups

		this.bounds = {
			container: null,
			section: null
		};

		this.coords = {
			actual: {
				x: -1,
				y: -1
			},
			last: {
				x: -1,
				y: -1
			},
			lerp: {
				x: -1,
				y: -1
			}
		};

		this.controllers = {
			animation: null,
			resize: null
		};

		this.flags = {
			isAccelerating: true,
			isContained: false,
			isListening: false,
			isPaused: false
		};

		this.states = {
			lerp: 0
		};

		// class variable singles

		// engage
		this.init();
	}

	static get selector() {
		return selector;
	}

	// initialize class

	init() {
		if (this.el.section.classList.contains('is-contained')) {
			// don't auto instantiate if contained
			this.el.container = this.el.section.parentElement;
			this.flags.isContained = true;
		} else {
			this.addEventListeners();
		}
	}

	// initialize from controller

	start() {
		if (this.flags.isListening) {
			this.resume();
		} else {
			this.addEventListeners();
		}
	}

	// event listeners

	addEventListeners() {
		if (!this.flags.isListening) {
			this.flags.isListening = true;

			this.controllers.animation = AnimationController.set({
				update: this.events._onAnimationUpdate,
				render: this.events._onAnimationRender
			});

			this.controllers.resize = ResizeController.set({
				update: this.events._onResizeUpdate,
				render: this.events._onResizeRender
			});

			this.measureResize();
			this.measureCursor();

			this.renderCursor();
		}
	}

	removeEventListeners() {
		if (this.flags.isListening) {
			this.flags.isListening = false;

			AnimationController.clear(this.controllers.animation);
			this.controllers.animation = null;

			ResizeController.clear(this.controllers.resize);
			this.controllers.resize = null;
		}
	}

	// move cursor

	_onAnimationUpdate() {
		return this.measureCursor();
	}

	_onAnimationRender() {
		this.renderCursor();
	}

	measureCursor() {

		if (!mq('medium')) { return false; }

		if (!this.flags.isContained) {
			this.coords.actual.x = CursorController.coords.scrollX;
			this.coords.actual.y = CursorController.coords.scrollY;
		}

		if (this.coords.last.x === -1 || this.coords.last.y === -1) {
			this.coords.last.x = this.coords.actual.x;
			this.coords.last.y = this.coords.actual.y;
		}

		// lerp the lerp

		if (this.config.lerp.double) {
			this.states.lerp += (this.config.lerp.max - this.states.lerp) * this.config.lerp.acceleration;
		} else {
			this.states.lerp = this.config.lerp.max;
		}

		// get lerped cursor x and y

		const lastLerpX = this.coords.lerp.x;
		const lastLerpY = this.coords.lerp.y;

		this.coords.lerp.x = this.coords.last.x + (this.coords.actual.x - this.coords.last.x) * this.states.lerp;
		this.coords.lerp.y = this.coords.last.y + (this.coords.actual.y - this.coords.last.y) * this.states.lerp;

		return (lastLerpX !== this.coords.lerp.x && lastLerpY !== this.coords.lerp.y);
	}

	renderCursor() {
		this.el.section.style.left = `${this.coords.lerp.x - this.bounds.section.width / 2 + this.config.offset.x}px`;
		this.el.section.style.top = `${this.coords.lerp.y - this.bounds.section.height / 2 + this.config.offset.y}px`;

		this.coords.last.x = this.coords.lerp.x;
		this.coords.last.y = this.coords.lerp.y;
	}

	// resize calculations

	_onResizeUpdate() {
		return this.measureResize();
	}

	_onResizeRender() { }

	measureResize() {
		this.bounds.section = getOffsetBounds(this.el.section);

		if (this.flags.isContained) {
			this.bounds.container = getRootOffsetBounds(this.el.container);
		}

		return false;
	}

	// move to specific oordinates

	moveTo(x, y) {
		this.coords.actual = { x, y };
	}

	// slow down

	slow() {
		this.states.lerp = 0;
	}

	//

	pause() {
		this.flags.isPaused = true;
	}

	resume() {
		this.flags.isPaused = false;
	}

	// destroy

	destroy() {
		this.removeEventListeners();
	}

	// getters

	get offset() {
		return this.config.offset;
	}

}
